import React, { useState, useEffect } from 'react';
import { Addon, Adjustment } from './interfaces';
import AdjustmentsList from './AdjustmentsList';
import { Report } from 'powerbi-client';
import { ReactComponent as PlusSvg } from '../../../../assets/plus.svg';
import SetPicker from './SetPicker';
import AddAdjustmentModal from './AddAdjustmentModal';
import EditAdjustmentModal from './EditAdjustmentModal';
import { appInsights } from '../../../../App';
import { useAuthStore } from '../../../../features/authentication';

// Define types

const AdjustmentsEditor = ({
  setExternalSetNames,
  setExternalAdjustments,
  refreshReport,
  reportRef1,
  reportRef2,
  setNames,
  setExternalSelected,
  selectedSet,
}: {
  setExternalSetNames: (names: string[]) => void;
  setExternalAdjustments: (adjustments: Adjustment[]) => void;
  refreshReport: () => void;
  reportRef1: React.MutableRefObject<Report | null>;
  reportRef2: React.MutableRefObject<Report | null>;
  setNames: string[];
  setExternalSelected: (name: string) => void;
  selectedSet: string;
}) => {
  const { canDiscount, username } = useAuthStore();
  const [adjustments, setadjustments] = useState<Adjustment[]>([]);
  const [sets, setsets] = useState<string[]>([]);
  const [floorplanConfigurations, setfloorplanConfigurations] = useState<string[]>([]);
  const [itemNames, setItemNames] = useState<Addon[]>([]);
  const [adjustmentToEdit, setAdjustmentToEdit] = useState<Adjustment | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchAdjustments();
    fetchFloorplans();
    fetchItemNames();
  }, []);

  const handleEditClick = (adjustment: Adjustment) => {
    setAdjustmentToEdit(adjustment);
    setShowEditModal(true);
  };

  const handleSwitchToAdd = () => {
    setShowAddModal(true);

    appInsights.trackEvent({
      name: 'AddAdjustmentClick',
    });
  };

  const fetchAdjustments = async () => {
    try {
      const response = await fetch(
        `https://prod-49.eastus.logic.azure.com/workflows/dbe186fa92c04549a25406ee1aab0f84/triggers/manual/paths/invoke/${username}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=vZeMLAAyJUOaFlhm-6wQ34jhYs7A59my5wVkEgXceKs`
      );
      const data: Adjustment[] = await response.json();

      setadjustments(data);
      setExternalAdjustments(data);

      const setNames = Array.from(new Set(data.map(adjustment => adjustment.setname)));

      setsets(setNames);
      setExternalSetNames(setNames);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchItemNames = async () => {
    try {
      const response = await fetch(
        'https://prod-15.eastus.logic.azure.com:443/workflows/7c4ca801679645d3b3ee4fac2ae7f467/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=YTexlERDyM2RZb2cdv2dk0_o4gA57Ocfo7-uJv0WKDs'
      );
      const data: Addon[] = await response.json();

      setItemNames(data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchFloorplans = async () => {
    try {
      const response = await fetch(
        'https://prod-50.eastus.logic.azure.com:443/workflows/d0af1d23e4964910a93e123ff885eb31/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=TMC16bHSrFS5f8KVyoNp0wT9YMUZ50uIIC9T9SzVRc0'
      );
      const data = await response.json();

      let filteredArray = data.filter((item: string) => item !== '1x3' && item !== '1x4' && item !== '2x4');

      setfloorplanConfigurations(filteredArray);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleAddadjustment = async (adjustment: Adjustment) => {
    try {
      const { rowid, ...adjustmentWithoutRowid } = adjustment;

      appInsights.trackEvent({
        name: 'AddAdjustmentPopupSaveClick',
        properties: adjustmentWithoutRowid,
      });

      const response = await fetch(
        'https://prod-91.eastus.logic.azure.com:443/workflows/65498e4c80e34738b8d2cd02d81b0715/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=mhaC7kogG9saGV1FBxInFIHD4CWUQTmccQ2H1eJsico',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(adjustment),
        }
      );
      if (response.ok) {
        fetchAdjustments(); // Refresh the list of companies
        setShowAddModal(false); // Switch back to the adjustment list
        refreshReport();
      }
    } catch (error) {
      console.error('Error adding adjustment:', error);
    }
  };

  const handleDeleteAdjustment = async (rowid: number) => {
    try {
      const response = await fetch(
        `https://prod-62.eastus.logic.azure.com/workflows/c537deffe39740be9bda90be5786489b/triggers/manual/paths/invoke/${rowid}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=cYKg2CRYz1gSireX1voK8Sdpv6Z3oOULZFqHGEo4upE`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        fetchAdjustments(); // Refresh the list of companies
        refreshReport();
      }
    } catch (error) {
      console.error('Error deleting adjustment:', error);
    }
  };

  const handleEditadjustment = async (adjustment: Adjustment) => {
    try {
      const response = await fetch(
        `https://prod-62.eastus.logic.azure.com/workflows/c537deffe39740be9bda90be5786489b/triggers/manual/paths/invoke/${adjustment.rowid}?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=cYKg2CRYz1gSireX1voK8Sdpv6Z3oOULZFqHGEo4upE`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        try {
          const response = await fetch(
            'https://prod-91.eastus.logic.azure.com:443/workflows/65498e4c80e34738b8d2cd02d81b0715/triggers/manual/paths/invoke?api-version=2016-10-01&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=mhaC7kogG9saGV1FBxInFIHD4CWUQTmccQ2H1eJsico',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(adjustment),
            }
          );
          if (response.ok) {
            fetchAdjustments(); // Refresh the list of companies
            setShowEditModal(false); // Switch back to the adjustment list
            refreshReport();
          }
        } catch (error) {
          console.error('Error adding adjustment:', error);
        }
      }
    } catch (error) {
      console.error('Error deleting adjustment:', error);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'start',
        alignItems: 'baseline',
        width: '100%',
      }}
    >
      {showAddModal && (
        <AddAdjustmentModal
          onClose={() => {
            setShowAddModal(false);
          }}
          onAddAdjustment={handleAddadjustment}
          floorplans={floorplanConfigurations}
          itemNames={itemNames}
          setNames={sets}
          user={username}
          existingAdjustments={adjustments}
        />
      )}
      {showEditModal && adjustmentToEdit && (
        <EditAdjustmentModal
          onClose={() => {
            setShowEditModal(false);
          }}
          onEditAdjustment={handleEditadjustment}
          adjustmentToEdit={adjustmentToEdit}
          existingAdjustments={adjustments.filter(a => a.rowid !== adjustmentToEdit?.rowid)}
          floorplans={floorplanConfigurations}
          itemNames={itemNames}
          setNames={sets}
          user={username}
        />
      )}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          width: '100%',
          alignItems: 'center',
          gap: '0.8vh',
        }}
      >
        <SetPicker
          reportRef1={reportRef1}
          reportRef2={reportRef2}
          setNames={setNames}
          setExternalSelected={setExternalSelected}
          selectedSet={selectedSet}
        />
        <button style={styles.button} onClick={handleSwitchToAdd}>
          <PlusSvg fill="white" height={'2.1vh'} width={'2.1vh'} />
        </button>
      </div>
      <div style={{ height: '0.8vh' }} />
      <div style={styles.container(canDiscount())}>
        <AdjustmentsList
          adjustments={adjustments}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteAdjustment}
        />
      </div>
    </div>
  );
};

// Inline styles for simplicity
const styles = {
  container: (hasRole: boolean) => ({
    maxHeight: hasRole ? 'calc(100vh - 70vh)' : 'calc(100vh - 63vh)',
    minHeight: hasRole ? 'calc(100vh - 70vh)' : 'calc(100vh - 63vh)',
    overflowY: 'scroll' as const,
    width: '100%',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
    backgroundColor: '#fff',
    borderRadius: '1.6vh',
  }),
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '4vh',
    height: '4vh',
    borderRadius: '50%',
    backgroundColor: '#00AB9B', // Adjust the color to match the image
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
};

export default AdjustmentsEditor;
